import { Wand2 } from "lucide-react";
import { clsx } from "clsx";

export type AllotmentRoom = { id: string; name: string; roomNumber?: string | number | null; roomTypeName?: string | null };

const roomTitle = (room: AllotmentRoom) => String(room.roomNumber ?? room.name);
const roomSub = (room: AllotmentRoom) => (room.roomNumber ? room.name : "");

// Allots the rooms a booking needs. A booking for N rooms can only be confirmed once exactly N are picked;
// "Auto select" fills the first N free rooms of the requested type in one click.
export function RoomAllotmentPicker({ roomsNeeded, options, selected, loading, onChange, suggested, showType = false }: {
  roomsNeeded: number;
  options: AllotmentRoom[];
  selected: string[];
  loading: boolean;
  onChange: (roomIds: string[]) => void;
  /** Server's auto-allot (clean rooms, group kept together); used by "Auto select" when given. */
  suggested?: string[];
  /** Show each room's type (group bookings across types). */
  showType?: boolean;
}) {
  const enough = options.length >= roomsNeeded;
  const left = roomsNeeded - selected.length;
  const toggle = (roomId: string) => {
    if (selected.includes(roomId)) onChange(selected.filter((id) => id !== roomId));
    else if (roomsNeeded === 1) onChange([roomId]);
    else if (selected.length < roomsNeeded) onChange([...selected, roomId]);
  };
  const pickedNames = options.filter((room) => selected.includes(room.id)).map(roomTitle).join(" & ");

  return (
    <fieldset className="room-allotment">
      <div className="room-allotment__head">
        <legend>Allot {roomsNeeded} room{roomsNeeded === 1 ? "" : "s"}</legend>
        <button type="button" className="text-link" disabled={loading || !options.length} onClick={() => onChange(suggested?.length ? suggested : options.slice(0, roomsNeeded).map((room) => room.id))}>
          <Wand2 size={15} /> Auto select
        </button>
      </div>
      {loading ? (
        <div className="room-allotment__grid" aria-busy="true">{[0, 1, 2, 3].map((i) => <span key={i} className="room-chip room-chip--loading" />)}</div>
      ) : (
        <div className="room-allotment__grid">
          {options.map((room) => {
            const on = selected.includes(room.id);
            return (
              <button
                type="button" key={room.id} aria-pressed={on}
                className={clsx("room-chip", on && "is-selected")}
                disabled={!on && roomsNeeded > 1 && selected.length >= roomsNeeded}
                onClick={() => toggle(room.id)}
              >
                <strong className="num">{roomTitle(room)}</strong>
                <small>{on ? "Selected" : showType && room.roomTypeName ? room.roomTypeName : roomSub(room) || "Free"}</small>
              </button>
            );
          })}
        </div>
      )}
      {!loading && !enough ? (
        <small className="field-error">Only {options.length} of the {roomsNeeded} rooms needed are available for these dates, so this booking can't be confirmed.</small>
      ) : !loading && (
        <small className={clsx("room-allotment__hint", left === 0 && "is-ready")} aria-live="polite">
          {left === 0 ? `Ready — room${roomsNeeded === 1 ? "" : "s"} ${pickedNames} selected` : `Select ${left} more room${left === 1 ? "" : "s"} to confirm`}
        </small>
      )}
    </fieldset>
  );
}
